import { useEffect, useState, useRef, useCallback } from "react";
import { Container, TextField, Button, Grid, Typography, ButtonGroup, CardHeader } from "@mui/material";
import { useAuth } from "../hooks/useAuth";
import { GetTokenIdentity } from "../utils/Token";
import { Icons } from "../utils/Icon";
import { CustomStyles } from "../utils/Styled";

import axios from "axios";
import MaterialTable from "material-table";

import CreateProductGroupForm from "../components/forms/productGroups/Create";
import EditProductGroupForm from "../components/forms/productGroups/Edit";
import DeleteProductGroupForm from "../components/forms/productGroups/Delete";
import ReminderConfigForm from "../components/forms/productGroups/ReminderConfig";

import CountrySelect from "../components/common/CountrySelect";

export const ProductGroupsPage = () => {
    const { token, logout } = useAuth();
    const identity = GetTokenIdentity(token);

    const classes = CustomStyles;

    const nameInputRef = useRef(null);
    const descriptionInputRef = useRef(null);

    const [data, setData] = useState([]);
    const [count, setCount] = useState(0);
    const [page, setPage] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [filter, setFilter] = useState({ country_id: identity.user.country_id });
    const [sort, setSort] = useState({});

    const [userCountry, setUserCountry] = useState([]);

    useEffect(() => {
        async function fetchCountryData() {
            const response = await axios.get(process.env.REACT_APP_WEBSITE_BACKEND_URL + "api/v1/users/access-country");
            setUserCountry(response.data.data.countries);
        }
        fetchCountryData();
    }, []);

    const [open, setOpen] = useState({
        create: false,
        edit: false,
        reminderConfig: false,
    });

    const [selectedRow, setSelectedRow] = useState({});
    const [selectedReminderType, setSelectedReminderType] = useState("first");

    const handleOpen = (type, row, reminderType = null) => {
        setOpen({ ...open, [type]: true });
        setSelectedRow(row);
        if (reminderType) {
            setSelectedReminderType(reminderType);
        }
    };

    const handleClose = (type) => {
        setOpen({ ...open, [type]: false });
        setSelectedRow(null);
    };

    const handleResetFilter = () => {
        setPage(0);
        setFilter({ country_id: identity.user.country_id });
    };

    const handleFilterChange = (event) => {
        setPage(0);
        setFilter({ ...filter, [event.target.name]: event.target.value });
    };

    const fetchData = useCallback(async () => {
        try {
            const { data } = await axios.get(process.env.REACT_APP_WEBSITE_BACKEND_URL + "api/v1/product-groups", {
                params: {
                    page: page + 1,
                    perPage,
                    filter: JSON.stringify(filter),
                    sort,
                },
            });
            setData(data.data.productGroups);
            setCount(data.data.total);
        } catch (error) {
            if (error.response?.status === 401) {
                console.log("Unauthorized: ", error);
                logout("Your account has been logged out of a session.");
            } else {
                console.log(error);
            }
        }
    }, [page, perPage, filter, sort, logout]);

    useEffect(() => {
        fetchData();
    }, [fetchData, page, perPage, filter, sort]);

    const columns = [
        {
            title: "Name",
            field: "group_name",
            cellStyle: { width: 320, maxWidth: 320 },
            headerStyle: { width: 320, maxWidth: 320 },
        },
        { title: "Description", field: "group_description" },
        {
            title: "Reminder Config",
            sorting: false,
            filtering: false,
            render: (rowData) =>
                identity.user.role_id === 1 || identity.user.role_id === 2 ? (
                    <ButtonGroup size="small" variant="outlined" aria-label="reminder-config-actions">
                        <Button onClick={() => handleOpen("reminderConfig", rowData, "first")}>First</Button>
                        <Button onClick={() => handleOpen("reminderConfig", rowData, "second")}>Second</Button>
                        <Button onClick={() => handleOpen("reminderConfig", rowData, "expired")}>Expired</Button>
                    </ButtonGroup>
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        -
                    </Typography>
                ),
        },
    ];

    const actions = [];

    if (identity.user.role_id === 1 || identity.user.role_id === 2) {
        actions.push({
            icon: () => <Icons.Edit />,
            tooltip: "Edit Product Group Information",
            onClick: (event, rowData) => handleOpen("edit", rowData),
        });
        actions.push({
            icon: () => <Icons.Delete />,
            tooltip: "Delete Product Group",
            onClick: (event, rowData) => DeleteProductGroupForm(rowData, fetchData),
        });
    }

    return (
        <div>
            <div className={classes.background} />
            <Container maxWidth="xl" sx={{ py: 2 }} style={{ padding: 14 }}>
                <MaterialTable
                    title="Product Group List"
                    icons={Icons}
                    columns={columns}
                    data={data}
                    page={page}
                    totalCount={count}
                    style={{ padding: "24px" }}
                    options={{
                        pageSize: perPage,
                        pageSizeOptions: [10, 20, 30, 50],
                        search: false,
                        filtering: false,
                        sorting: false,
                        actionsColumnIndex: -1,
                        headerStyle: {
                            fontWeight: "bold",
                            backgroundColor: "#f1f1f1",
                            color: "#212122",
                        },
                        maxBodyHeight: "600px",
                        paginationType: "stepped",
                        padding: "dense",
                        draggable: false,
                    }}
                    localization={{
                        pagination: {
                            labelRowsPerPage: "",
                            labelDisplayedRows: "{from}-{to} of {count}",
                            labelRowsSelect: "rows",
                        },
                        toolbar: {
                            nRowsSelected: "{0} row(s) selected",
                        },
                        header: {
                            actions: "Actions",
                        },
                        body: {
                            emptyDataSourceMessage: "No data to display",
                            filterRow: {
                                filterTooltip: "Filter",
                            },
                        },
                    }}
                    actions={actions}
                    onChangePage={(nextPage) => setPage(nextPage)}
                    onChangeRowsPerPage={(nextPerPage) => setPerPage(nextPerPage)}
                    components={{
                        Toolbar: () => (
                            <>
                                <div>
                                    <Grid container spacing={2} sx={{ px: 0.1, pb: 3.5 }}>
                                        <Grid item xs={12} sm={3}>
                                            <CardHeader
                                                sx={{ p: 0 }}
                                                title={
                                                    <Typography component="h1" variant="h5" color="primary" fontWeight="bold">
                                                        Product Group List
                                                    </Typography>
                                                }
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={9} style={{ textAlign: "right" }}>
                                            {(identity.user.role_id === 1 || identity.user.role_id === 2) && (
                                                <ButtonGroup>
                                                    <Button
                                                        onClick={() => handleOpen("create", null)}
                                                        variant="outlined"
                                                        startIcon={<Icons.AddIcon fontWeight="bold" />}
                                                        color="primary"
                                                        sx={{
                                                            fontWeight: 800,
                                                            paddingTop: 0.5,
                                                            paddingBottom: 0.5,
                                                        }}
                                                    >
                                                        Create
                                                    </Button>
                                                </ButtonGroup>
                                            )}
                                        </Grid>
                                    </Grid>
                                </div>

                                <form>
                                    <Grid container spacing={2} sx={{ paddingBottom: 1.5 }}>
                                        <Grid item xs={12} sm={3} md={3}>
                                            <TextField
                                                autoComplete="off"
                                                fullWidth
                                                label="Name"
                                                name="group_name"
                                                variant="outlined"
                                                autoFocus={nameInputRef.current === document.activeElement}
                                                value={filter.group_name || ""}
                                                onChange={handleFilterChange}
                                                inputRef={nameInputRef}
                                                size="small"
                                                InputLabelProps={{
                                                    shrink: true,
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={5}>
                                            <TextField
                                                autoComplete="off"
                                                fullWidth
                                                label="Description"
                                                name="group_description"
                                                variant="outlined"
                                                autoFocus={descriptionInputRef.current === document.activeElement}
                                                value={filter.group_description || ""}
                                                onChange={handleFilterChange}
                                                inputRef={descriptionInputRef}
                                                size="small"
                                                InputLabelProps={{
                                                    shrink: true,
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={3} md={1.5} xl={1.5}>
                                            <CountrySelect value={filter.country_id === undefined ? "" : filter.country_id} onChange={handleFilterChange} />
                                        </Grid>
                                        <Grid item xs={12} sm={1}>
                                            <Button
                                                fullWidth
                                                variant="contained"
                                                color="error"
                                                sx={{
                                                    color: "#ffffff",
                                                    marginTop: "2px",
                                                }}
                                                onClick={handleResetFilter}
                                            >
                                                Reset
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </form>
                            </>
                        ),
                    }}
                />
                <CreateProductGroupForm
                    open={open.create}
                    handleClose={() => handleClose("create")}
                    fetchData={fetchData}
                    userCountry={userCountry}
                    defaultCountryId={identity.user.country_id}
                />
                <EditProductGroupForm open={open.edit} handleClose={() => handleClose("edit")} rowData={selectedRow} fetchData={fetchData} userCountry={userCountry} />
                <ReminderConfigForm
                    open={open.reminderConfig}
                    handleClose={() => handleClose("reminderConfig")}
                    rowData={selectedRow}
                    reminderType={selectedReminderType}
                    fetchData={fetchData}
                />
            </Container>
        </div>
    );
};
